import React from "react";
import { useFormik } from "formik";
import {
  Button,
  Grid,
  Stack,
  Typography,
  Box,
  Paper,
  Divider,
  useTheme,
  SelectChangeEvent,
} from "@mui/material";
import {
  Save,
  Description,
  CalendarToday,
  AttachMoney,
  Policy,
  CheckCircle,
  ErrorOutline,
} from "@mui/icons-material";
import {
  ControlledCustomSelect,
  CustomFormLabel,
} from "../../../../../../components/formsComponents";
import { CustomDatePicker } from "../../../../../../components/formsComponents/CustomDatePicker";
import { FileUpload } from "../../../../../../features/FileUpload/components/FileUpload";
import { INITIAL_POLICY_UPDATES } from "./PolicyUpdatesList";
import { StandadCard } from "../../../../../../components/Cards";
import { CircularProgress } from "@mui/material";
import { CSS_CONSTANTS } from "../../../../../../constants/cssConstants";
import { IPolicyUpdates } from "../../../../../../redux/policyCommon/policyCommon.types";
import { truncateToDecimals } from "../../../../../../helpers/truncateToDecimals";
import { LIMITS } from "./PolicyUpdatesRightPanel";
import produce from "immer";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../../../../../../api/api";
import { useDispatchWrapper } from "../../../../../../hooks";
import { upsertPolicyUpdatesFromPublicFormAsync } from "../../../../../../redux/policyCommon/policyCommonActions";

export const PublicPolicyUpdatesForm: React.FC = () => {
  const { key } = useParams() as { key: string };
  const [file, setFile] = React.useState<File | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const theme = useTheme();
  const dispatch = useDispatchWrapper();
  const navigate = useNavigate();

  const {
    values,
    errors,
    setFieldValue,
    handleChange,
    setValues,
    handleSubmit,
  } = useFormik({
    initialValues: INITIAL_POLICY_UPDATES,
    onSubmit: (values) => {
      dispatch(
        upsertPolicyUpdatesFromPublicFormAsync({...values, status: "Submitted"}, file,key, (isSuccess) => {
          if (isSuccess) {
            navigate(`/policy-updates/${key}/thank-you`);
          }
        })
      );
    },
  });

  const [loading, setLoading] = React.useState(true);

  const calcTotal = (values: IPolicyUpdates) => {
    if (values.limit_of_liability && values.gross_revenue) {
      if (values.gross_revenue === "Less than $175,000") {
        values.premium =
          LIMITS["Less than $175,000"][
            values.limit_of_liability as keyof typeof LIMITS["Less than $175,000"]
          ].premium;
      } else {
        values.premium =
          LIMITS["Greater than $175,000"][
            values.limit_of_liability as keyof typeof LIMITS["Greater than $175,000"]
          ].premium;
      }

      if (values.province) {
        const taxRate = parseFloat(values.tax || "0");
        const premium = values.premium || 0;
        const tax = (premium * taxRate) / 100;
        values.total_payable = truncateToDecimals(premium + tax).toString();
      }
    }
  };

  const handleProvinceChange = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value as string;
    const newValues = produce(values, (draft) => {
      draft.province = value;
      if (value === "ON") {
        draft.tax = "8";
      } else {
        draft.tax = "0";
      }
      calcTotal(draft);
    });
    setValues(newValues);
  };

  const handleGrossRevenueChange = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value as string;
    const newValues = produce(values, (draft) => {
      draft.gross_revenue = value;
      calcTotal(draft);
    });
    setValues(newValues);
  };

  const handleLimitsofLiabilityChange = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value as string;
    const newValues = produce(values, (draft) => {
      draft.limit_of_liability = value;

      calcTotal(draft);
    });
    setValues(newValues);
  };

  React.useEffect(() => {
    (async () => {
      try {
        const response = await api.get(`/policy/get-policy-update-external`, {
          headers: {
            "auth-key": key,
          },
        });

        if (response.data.data.length === 0) {
          setError(
            "This link has expired—please generate a new policy update link."
          );
          return;
        } else {
          setValues(response.data.data[0]);
        }
      } catch (err) {
        setError("Failed to load policy update data. Please try again later.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: theme.palette.grey[200],
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" sx={{ mt: 2, color: "text.secondary" }}>
            Loading...
          </Typography>
        </Box>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: theme.palette.grey[200],
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 4,
          px: 2,
        }}
      >
        <Box sx={{ maxWidth: 600, mx: "auto" }}>
          <StandadCard>
            <Box sx={{ textAlign: "center", py: 4 }}>
              <ErrorOutline sx={{ fontSize: 80, color: "error.main", mb: 2 }} />
              <Typography
                variant="h5"
                fontWeight={600}
                color="error.main"
                gutterBottom
              >
                Record Not Found
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                {error}
              </Typography>
            </Box>
          </StandadCard>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: theme.palette.grey[200],
        py: 4,
        px: 2,
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        <StandadCard>
          {/* Header Section */}
          <Stack direction="row" alignItems="center" spacing={2} mb={2}>
            <Policy sx={{ fontSize: 40, color: "#667eea" }} />
            <Box>
              <Typography
                variant="body1"
                fontWeight={700}
                fontSize={CSS_CONSTANTS.MAIN_HEADING_3}
                sx={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Policy Renewal Update Request
              </Typography>
              <Typography
                variant="body2"
                fontSize={CSS_CONSTANTS.LABEL_VALUE_SIZE}
                color="text.secondary"
              >
                Review and update your policy information
              </Typography>
            </Box>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={1} mb={3}>
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Description color="primary" />
                <Box>
                  <Typography
                    variant="body1"
                    fontSize={CSS_CONSTANTS.LABEL_VALUE_SIZE}
                  >
                    Named Insured
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    fontSize={CSS_CONSTANTS.LABEL_HEADING_SIZE}
                  >
                    {values.customer_name || "N/A"}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Description color="primary" />
                <Box>
                  <Typography
                    variant="body1"
                    fontSize={CSS_CONSTANTS.LABEL_VALUE_SIZE}
                  >
                    Policy Number
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    fontSize={CSS_CONSTANTS.LABEL_HEADING_SIZE}
                  >
                    {values.policy_number || "N/A"}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>

          <form onSubmit={handleSubmit}>
            {/* Policy Details Section */}
            <Box
              sx={{
                p: 2,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: 1,
                mb: 2,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <CalendarToday sx={{ color: "white" }} />
                <Typography
                  variant="body1"
                  fontWeight={600}
                  fontSize={CSS_CONSTANTS.LABEL_HEADING_SIZE}
                  color="white"
                >
                  Policy Period & Other
                </Typography>
              </Stack>
            </Box>

            <Grid container spacing={1} mb={3}>
              <Grid item xs={12} md={6}>
                <CustomFormLabel>Effective Date</CustomFormLabel>
                <CustomDatePicker
                  value={values.effective_date || ""}
                  fullWidth
                  onChange={(value) => setFieldValue("effective_date", value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomFormLabel>Expiry Date</CustomFormLabel>
                <CustomDatePicker
                  value={values.expiry_date || ""}
                  fullWidth
                  onChange={(value) => setFieldValue("expiry_date", value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomFormLabel>Province</CustomFormLabel>
                <ControlledCustomSelect
                  fullWidth
                  value={values.province}
                  name="province"
                  onChange={handleProvinceChange}
                  placeholder="Select Province"
                  displayEmpty
                  options={["AB", "ON"].map((template) => {
                    return { label: template, value: template };
                  })}
                />
              </Grid>
             
            </Grid>

            {/* Revenue Section */}
            <Box
              sx={{
                p: 2,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: 1,
                mb: 2,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <CheckCircle sx={{ color: "white" }} />
                <Typography
                  variant="body1"
                  fontWeight={600}
                  fontSize={CSS_CONSTANTS.LABEL_HEADING_SIZE}
                  color="white"
                >
                  Select Revenue
                </Typography>
              </Stack>
            </Box>

            <Grid container spacing={1} mb={3}>
              <Grid item xs={12} md={6}>
                <CustomFormLabel>Gross Revenue</CustomFormLabel>
                <ControlledCustomSelect
                  fullWidth
                  value={values.gross_revenue}
                  name="gross_revenue"
                  onChange={handleGrossRevenueChange}
                  placeholder="Select Gross Revenue Range"
                  displayEmpty
                  options={[
                    { label: "Less than $175,000", value: "Less than $175,000" },
                    {
                      label: "Greater than $175,000",
                      value: "Greater than $175,000",
                    },
                  ].map((item) => {
                    return { label: item.label, value: item.value };
                  })}
                />
              </Grid>
          
            </Grid>

                     {/* Coverage  Section */}
            <Box
              sx={{
                p: 2,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: 1,
                mb: 2,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <CheckCircle sx={{ color: "white" }} />
                <Typography
                  variant="body1"
                  fontWeight={600}
                  fontSize={CSS_CONSTANTS.LABEL_HEADING_SIZE}
                  color="white"
                >
                  Select Coverage
                </Typography>
              </Stack>
            </Box>

            <Grid container spacing={1} mb={3}>
             
              <Grid item xs={12} md={6}>
                <CustomFormLabel>Limits of Liability</CustomFormLabel>
                <ControlledCustomSelect
                  fullWidth
                  value={values.limit_of_liability}
                  name="limit_of_liability"
                  onChange={handleLimitsofLiabilityChange}
                  placeholder="Select Liability Limits"
                  displayEmpty
                  options={[
                    {
                      label: "$1,000,000 / $2,000,000",
                      value: "$1,000,000 / $2,000,000",
                    },
                    {
                      label: "$1,000,000 / $5,000,000",
                      value: "$1,000,000 / $5,000,000",
                    },
                    {
                      label: "$2,000,000 / $2,000,000",
                      value: "$2,000,000 / $2,000,000",
                    },
                    {
                      label: "$2,000,000 / $5,000,000",
                      value: "$2,000,000 / $5,000,000",
                    },
                  ].map((item) => {
                    return { label: item.label, value: item.value };
                  })}
                />
              </Grid>
            </Grid>



            {/* Payment Summary Section */}
            <Box
              sx={{
                p: 2,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: 1,
                mb: 2,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <AttachMoney sx={{ color: "white" }} />
                <Typography
                  variant="body1"
                  fontWeight={600}
                  fontSize={CSS_CONSTANTS.LABEL_HEADING_SIZE}
                  color="white"
                >
                  Payment Summary
                </Typography>
              </Stack>
            </Box>

            <Grid container spacing={1} mb={3}>
              <Grid item xs={12} md={4}>
                <Paper
                  sx={{
                    p: 3,
                    textAlign: "center",
                    background:
                      "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    fontSize={CSS_CONSTANTS.LABEL_VALUE_SIZE}
                    color="text.secondary"
                  >
                    Premium
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight={700}
                    fontSize={CSS_CONSTANTS.LABEL_HEADING_SIZE}
                    color="primary"
                  >
                    {values.premium || "$0.00"}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper
                  sx={{
                    p: 3,
                    textAlign: "center",
                    background:
                      "linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    fontSize={CSS_CONSTANTS.LABEL_VALUE_SIZE}
                    color="text.secondary"
                  >
                    Tax
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight={700}
                    fontSize={CSS_CONSTANTS.LABEL_HEADING_SIZE}
                    color="secondary"
                  >
                    {values.tax || "$0.00"}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper
                  sx={{
                    p: 3,
                    textAlign: "center",
                    background:
                      "linear-gradient(135deg, #c8e6c9 0%, #a5d6a7 100%)",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    fontSize={CSS_CONSTANTS.LABEL_VALUE_SIZE}
                    color="text.secondary"
                  >
                    Total Payable
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight={700}
                    fontSize={CSS_CONSTANTS.LABEL_HEADING_SIZE}
                    color="success.dark"
                  >
                    {values.total_payable || "$0.00"}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                  <Box
              sx={{
                p: 2,
                mt: 2,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: 1,
                mb: 2,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <AttachMoney sx={{ color: "white" }} />
                <Typography
                  variant="body1"
                  fontWeight={600}
                  fontSize={CSS_CONSTANTS.LABEL_HEADING_SIZE}
                  color="white"
                >
                  Payment Options
                </Typography>
              </Stack>
            </Box>
                <CustomFormLabel>Interac e-Transfer</CustomFormLabel>
                 <Typography variant="body1" fontSize={CSS_CONSTANTS.LABEL_HEADING_SIZE} fontWeight={600}>
                              {values.link_for_payment || "--"}
                            </Typography>
              </Grid>
            </Grid>

            {/* Attachments Section */}
            <Box
              sx={{
                p: 2,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: 1,
                mb: 2,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Description sx={{ color: "white" }} />
                <Typography
                  variant="body1"
                  fontWeight={600}
                  fontSize={CSS_CONSTANTS.LABEL_HEADING_SIZE}
                  color="white"
                >
                 Attachments
                </Typography>
              </Stack>
            </Box>

            <Box mb={3}>
              <FileUpload
                value={values.attachment}
                onChange={(file) => {
                  setFile(file);
                }}
                onDelete={() => {
                  setFile(null);
                  setValues({
                    ...values,
                    attachment: null,
                  });
                }}
              />
            </Box>

            {/* Action Buttons */}
            <Divider sx={{ my: 3 }} />
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="flex-end"
            >
              <Button
                variant="contained"
                type="submit"
                size="large"
                startIcon={<Save />}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)",
                  },
                }}
              >
                Submit
              </Button>
            </Stack>
          </form>
        </StandadCard>
      </Box>
    </Box>
  );
};
